import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Switch,
  Typography
} from '@mui/material';
import TopologyColumn from './topologyColumn';
import {
  getAmount,
  getHighestConnectionValue,
  JSONRecordData
} from '../Log/graph.util';
import { FormattedMessage } from 'react-intl';
import useIsMobile from '../../../hooks/useIsMobile';
import MobileEnergyHub, { HubRole } from './MobileEnergyHub';

interface TopologySodistoreGridProps {
  values: JSONRecordData;
  connected: boolean;
  loading: boolean;
}

function TopologySodistoreGrid(props: TopologySodistoreGridProps) {
  if (props.values === null && props.connected == true) {
    return null;
  }

  const highestConnectionValue =
    props.values != null ? getHighestConnectionValue(props.values) : 0;

  const [showValues, setShowValues] = useState(false);

  const handleSwitch = () => () => {
    setShowValues(!showValues);
  };

  const isMobile = window.innerWidth <= 1490;
  const isPhone = useIsMobile();

  const inv = props.values?.InverterRecord;
  const dcdc = props.values?.DcDc;
  const clusters = props.values?.ListOfBatteriesRecord ?? {};
  const clusterIds = Object.keys(clusters).sort(
    (a, b) => Number(a) - Number(b)
  );

  const acDcPower = Number(inv?.ActivePowerW ?? 0);
  const dcLinkPower = Number(dcdc?.Dc?.Link?.Power ?? 0);
  const dcLinkVoltage = Number(dcdc?.Dc?.Link?.Voltage ?? 0);
  const dcdcBatteryVoltage = Number(dcdc?.Dc?.Battery?.Voltage ?? 0);
  const dcdcBatteryPower = Number(dcdc?.Dc?.Battery?.Power ?? 0);

  // Mobile: aggregate all battery clusters into ONE box so the whole diagram
  // fits the phone width (per-cluster columns would overflow). avg SOC/voltage/
  // temperature, sum current.
  const clusterList = clusterIds.map((id) => clusters[id]).filter(Boolean);
  const cAvg = (f: (c: any) => number) =>
    clusterList.length
      ? clusterList.reduce((s, c) => s + (Number(f(c)) || 0), 0) /
        clusterList.length
      : 0;
  const cSum = (f: (c: any) => number) =>
    clusterList.reduce((s, c) => s + (Number(f(c)) || 0), 0);
  const aggSoc = cAvg((c) => c.Soc);
  const aggVoltage = cAvg((c) => c.Voltage);
  const aggCurrent = cSum((c) => c.Current);
  const aggTemp = cAvg((c) => c.TemperatureCell1);

  // Phone view. SodistoreGrid is grid-scale storage: no PV, no local loads, so the
  // hub renders just the two roles that exist. AC-DC is the grid-side converter, so
  // its power is what crosses the grid boundary.
  const hubRoles: HubRole[] = [
    { key: 'grid', power: acDcPower },
    {
      key: 'battery',
      power: dcdcBatteryPower,
      soc: aggSoc,
      detail: clusterList.length ? (
        <>
          {clusterIds.map((id) => {
            const c = clusters[id];
            return (
              <Box
                key={id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 0.5,
                  borderBottom: '1px solid #f0f0f0'
                }}
              >
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                  {id}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#5b6b7c' }}>
                  {Number(c?.Soc ?? 0).toFixed(0)} % ·{' '}
                  {Number(c?.Voltage ?? 0).toFixed(1)} V ·{' '}
                  {Number(c?.Current ?? 0).toFixed(1)} A ·{' '}
                  {Number(c?.TemperatureCell1 ?? 0).toFixed(1)} °C
                </Typography>
              </Box>
            );
          })}
        </>
      ) : undefined
    }
  ];

  return (
    <Container maxWidth="xl" style={{ backgroundColor: 'white' }}>
      <Grid container>
        {!props.connected && !props.loading && (
          <Container
            maxWidth="xl"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '70vh'
            }}
          >
            <CircularProgress size={60} style={{ color: '#ffc04d' }} />
            <Typography
              variant="body2"
              style={{ color: 'black', fontWeight: 'bold' }}
              mt={2}
            >
              <FormattedMessage
                id="unableToCommunicate"
                defaultMessage="Unable to communicate with the installation"
              />
            </Typography>
            <Typography variant="body2" style={{ color: 'black' }}>
              <FormattedMessage
                id="pleaseWaitOrRefresh"
                defaultMessage="Please wait or refresh the page"
              />
            </Typography>
          </Container>
        )}

        {props.connected && (
          <>
            <Grid
              item
              xs={12}
              md={12}
              style={{
                marginTop: '10px',
                height: '20px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'right',
                justifyContent: 'right'
              }}
            >
              <div>
                <Typography sx={{ marginTop: '5px', marginRight: '20px' }}>
                  Display Values
                </Typography>
                <Switch
                  edge="start"
                  color="secondary"
                  onChange={handleSwitch()}
                  sx={{
                    '& .MuiSwitch-thumb': {
                      backgroundColor: 'orange'
                    },
                    marginLeft: '20px'
                  }}
                />
              </div>
            </Grid>

            {isPhone ? (
              <Grid item xs={12}>
                <MobileEnergyHub roles={hubRoles} />
              </Grid>
            ) : (
            <Grid
              item
              xs={12}
              md={12}
              style={{
                height: isPhone ? 'auto' : isMobile ? '550px' : '600px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                // Phone: wrap wide diagrams to more rows so everything is visible
                // without horizontal scrolling; desktop stays one line.
                flexWrap: isPhone ? 'wrap' : 'nowrap',
                rowGap: isPhone ? '14px' : undefined
              }}
            >
              {/* AC-DC */}
              <TopologyColumn
                centerBox={{
                  title: 'AC-DC',
                  data: inv
                    ? [{ value: acDcPower, unit: 'W' }]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: inv ? { value: acDcPower, unit: 'W' } : undefined,
                  amount: inv
                    ? getAmount(highestConnectionValue, acDcPower)
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={true}
              />

              {/* DC Link */}
              <TopologyColumn
                centerBox={{
                  title: 'DC Link',
                  data: dcdc
                    ? [{ value: dcLinkVoltage, unit: 'V' }]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: dcdc
                    ? { value: dcLinkPower, unit: 'W' }
                    : undefined,
                  amount: dcdc
                    ? getAmount(highestConnectionValue, dcLinkPower)
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />

              {/* DC-DC */}
              <TopologyColumn
                centerBox={{
                  title: 'DC-DC',
                  data: dcdc
                    ? [{ value: dcdcBatteryVoltage, unit: 'V' }]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: dcdc
                    ? { value: dcdcBatteryPower, unit: 'W' }
                    : undefined,
                  amount: dcdc
                    ? getAmount(highestConnectionValue, dcdcBatteryPower)
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />

              {/* Battery: one column per cluster on desktop; a single aggregated
                  box on phones so the whole diagram fits the screen width. */}
              {isPhone ? (
                <TopologyColumn
                  centerBox={{
                    title: 'Battery',
                    data: clusterList.length
                      ? [
                          { value: aggSoc, unit: '%' },
                          { value: aggVoltage, unit: 'V' },
                          { value: aggCurrent, unit: 'A' },
                          { value: aggTemp, unit: '°C' }
                        ]
                      : undefined,
                    connected: true
                  }}
                  isLast={true}
                  isFirst={false}
                />
              ) : (
                clusterIds.map((id) => {
                  const c = clusters[id];
                  return (
                    <TopologyColumn
                      key={`battery-cluster-${id}`}
                      centerBox={{
                        title: `Battery ${id}`,
                        data: c
                          ? [
                              { value: Number(c.Soc ?? 0), unit: '%' },
                              { value: Number(c.Voltage ?? 0), unit: 'V' },
                              { value: Number(c.Current ?? 0), unit: 'A' },
                              {
                                value: Number(c.TemperatureCell1 ?? 0),
                                unit: '°C'
                              }
                            ]
                          : undefined,
                        connected: true
                      }}
                      isLast={true}
                      isFirst={false}
                    />
                  );
                })
              )}
            </Grid>
            )}
          </>
        )}
      </Grid>
    </Container>
  );
}

export default TopologySodistoreGrid;
